/*  ===================================================================
    MarketingBreakdown.js  •  v4.8  (2025-07-11)
    -------------------------------------------------------------------
    • Quick-delete button removed from task card
    • Completed tasks visually distinguished
    • Constant “General Costs” column (releaseId == null)
    • euro() helper fixes Babel error
    • Full, non-abbreviated source
    =================================================================== */

import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import emptyArt from '../assets/emptyart.jpg';
import invoiceIcon from '../assets/icons/invoice.svg';
import pencilIcon from '../assets/icons/pencil.svg';
import { api } from '../api';

/* ──────────────────────────────────────────────────────────────
   Tiny global style injection: remove Quill default borders
   ────────────────────────────────────────────────────────────── */
const quillNoBorderCSS = `
  .ql-toolbar, .ql-container { border: none !important; }
  .ql-editor { padding-left: .75rem; padding-right: .75rem; }
`;

function MarketingBreakdown({ projectId, releases = [], budget = 0, initialBudget = 0, reactiveBudgetsTotal = 0 }) {
  /* ╔════════════════════════════════════════════════╗
     ║  1. STATE                                      ║
     ╚════════════════════════════════════════════════╝ */
  const [tasks,          setTasks]          = useState([]);   // marketing tasks
  const [templates,      setTemplates]      = useState([]);   // campaign templates
  const [invoices,       setInvoices]       = useState([]);   // project-filtered invoices
  const [releaseBudgets, setReleaseBudgets] = useState({});   // { releaseId: number }

  /* modal & dialog state */
  const [showTaskModal,     setShowTaskModal]     = useState(false);
  const [editingTask,       setEditingTask]       = useState(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate,   setEditingTemplate]   = useState(null);
  const [showDetailsModal,  setShowDetailsModal]  = useState(false);
  const [detailsTask,       setDetailsTask]       = useState(null);
  
  /* inline editing state */
  // IMPORTANT: don't use null here, because a newly-created task can briefly have `_id: null`
  // which would accidentally force the UI into "editing" mode and make it impossible to exit.
  const [editingPlannedBudget, setEditingPlannedBudget] = useState(''); // taskId being edited
  const [plannedBudgetValue, setPlannedBudgetValue] = useState('');
  const [editingReleaseBudget, setEditingReleaseBudget] = useState(null); // releaseId being edited
  const [releaseBudgetValue, setReleaseBudgetValue] = useState('');

  /* drag-and-drop */
  const [draggingTaskId,  setDraggingTaskId]  = useState(null);
  const [dragOverRelease, setDragOverRelease] = useState(null);

  /* ╔════════════════════════════════════════════════╗
     ║  2. INITIAL FETCH                              ║
     ╚════════════════════════════════════════════════╝ */
  useEffect(() => {
    if (!projectId) return;

    /* marketing tasks */
    api(`/api/marketingtasks?projectId=${projectId}`)
      .then(r => r.json())
      .then(setTasks)
      .catch(console.error);

    /* templates */
    api('/api/marketingtemplates')
      .then(r => r.json())
      .then(setTemplates)
      .catch(console.error);

    /* invoices (filtered by project) */
    api(`/api/invoices?projectId=${projectId}`)
      .then(r => r.json())
      .then(setInvoices)
      .catch(console.error);

    /* release-level budgets */
    api(`/api/releasebudgets?projectId=${projectId}`)
      .then(r => r.json())
      .then(list => {
        const map = {};
        list.forEach(b => { map[b.releaseId] = b.budget; });
        setReleaseBudgets(map);
      })
      .catch(console.error);
  }, [projectId]);

  /* ╔════════════════════════════════════════════════╗
     ║  3. CONSTANTS & HELPERS                        ║
     ╚════════════════════════════════════════════════╝ */
  /* pseudo-release for non-specific costs */
  const GENERAL_RELEASE_ID = 'general';
  const generalRelease     = { _id: GENERAL_RELEASE_ID, title: 'General Costs' };

  /* releases list = General + sorted real releases */
  const releasesAll = [
    generalRelease,
    ...[...releases].sort((a, b) => {
      if (!a.releaseDate && !b.releaseDate) return 0;   // both undated
      if (!a.releaseDate) return 1;                     // a undated → after b
      if (!b.releaseDate) return -1;                    // b undated → after a
      return new Date(a.releaseDate) - new Date(b.releaseDate); // ascending
    }),
  ];

  /* currency helpers */
  const euro     = n => `€${Math.round(+n || 0)}`;
  const fmtLeft  = v => (v < 0 ? `-€${Math.round(Math.abs(v))}` : `€${Math.round(v)}`);
  const leftCol = v =>
    v < 0     ? '#d9534f'   // overspend → red
  : v === 0   ? '#ccc'      // exactly spent → grey
              : '#42c57b';  // budget left → green

  const statusBg = s =>
    ({ 'Not Started': '#888',
       'In Progress': '#5bc0de',
       'Completed'  : '#28a745' }[s] || '#555');

  /* tasks, invoices relationships */
  const getTasksForRelease = rid =>
    rid === GENERAL_RELEASE_ID
      ? tasks.filter(t => !t.releaseId)              // null / undefined
      : tasks.filter(t => String(t.releaseId) === String(rid));

  const getInvoicesForTask = tid =>
    invoices.filter(inv => {
      if (!inv.taskId) return false;
      const id = typeof inv.taskId === 'object' ? inv.taskId._id : inv.taskId;
      return String(id) === String(tid);
    });

  const getFinalForTask = task => {
    const invs = getInvoicesForTask(task._id);
    return invs.length
      ? invs.reduce((s, inv) => s + (+inv.amount || 0), 0)
      : +task.finalBudget || 0;
  };

  /* overall totals */
  const totalPlanned = tasks.reduce((s, t) => s + (+t.plannedBudget || 0), 0);
  const totalFinal   = tasks.reduce((s, t) => s + getFinalForTask(t), 0);

  /* ╔════════════════════════════════════════════════╗
     ║  4. CRUD HELPERS                               ║
     ╚════════════════════════════════════════════════╝ */
  const saveTaskToServer = (body, method, url) =>
    api(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then(r => r.json());

  /* ----- TASK MODAL ACTIONS ----- */
  const openNewTaskModal = releaseId => {
    setEditingTask({
      _id          : null,
      projectId,
      releaseId    : releaseId === GENERAL_RELEASE_ID ? null : releaseId,
      name         : '',
      plannedBudget: '',
      finalBudget  : '',
      status       : 'Not Started',
      notes        : '',
    });
    setShowTaskModal(true);
  };

  const handleSaveTask = async () => {
    if (!editingTask?.name.trim()) {
      alert('Task name is required');
      return;
    }
  
    const isUpdate = Boolean(editingTask && editingTask._id);
  
    try {
      if (isUpdate) {
        /* ---------- UPDATE existing ---------- */
        const updated = await saveTaskToServer(
          editingTask,                                   // keep _id in body
          'PUT',
          `/api/marketingtasks/${editingTask._id}`
        );
        setTasks(prev => prev.map(t => (t._id === updated._id ? updated : t)));
      } else {
        /* ---------- CREATE new ---------- */
        const planned = editingTask.plannedBudget === '' || editingTask.plannedBudget == null
          ? 0
          : Number(editingTask.plannedBudget);
        const final = editingTask.finalBudget === '' || editingTask.finalBudget == null
          ? 0
          : Number(editingTask.finalBudget);

        const created = await saveTaskToServer(
          {
            ...editingTask,
            plannedBudget: Number.isFinite(planned) ? planned : 0,
            finalBudget: Number.isFinite(final) ? final : 0,
          },
          'POST',
          '/api/marketingtasks'
        );

        // Safety: if the backend didn't return a persisted doc, refetch instead of inserting a broken row
        if (!created || !created._id) {
          const fresh = await api(`/api/marketingtasks?projectId=${projectId}`).then(r => r.json());
          setTasks(Array.isArray(fresh) ? fresh : []);
        } else {
          setTasks(prev => [...prev, created]);
        }
      }
  
      setShowTaskModal(false);
      setEditingTask(null);
    } catch (err) {
      console.error('Save task failed:', err);
      alert(err.message || 'Failed to save task');
    }
  };

  const deleteTaskById = async taskId => {
    await api(`/api/marketingtasks/${taskId}`, { method: 'DELETE' }).catch(console.error);
    setTasks(prev => prev.filter(t => t._id !== taskId));
  };

  const handleDeleteTask = async () => {
    if (!editingTask?._id) return;
    if (!window.confirm('Delete this task?')) return;
    await deleteTaskById(editingTask._id);
    setShowTaskModal(false);
    setEditingTask(null);
  };

  const handleStatusChange = async (taskId, newStatus) => {
    // 1. find the full task so we still have its releaseId and other fields
    const task = tasks.find(t => t._id === taskId);
    if (!task) return;
  
    // 2. optimistic UI update
    setTasks(prev =>
      prev.map(t =>
        t._id === taskId
          ? {
              ...t,
              status: newStatus,
              completedOn:
                newStatus === 'Completed' ? new Date().toISOString() : null,
            }
          : t
      )
    );
  
    // 3. send all fields (especially releaseId) so the server keeps the link
    try {
      await api(`/api/marketingtasks/${taskId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...task, // preserves releaseId, projectId, etc.
          status: newStatus,
          completedOn:
            newStatus === 'Completed' ? new Date().toISOString() : null,
        }),
      });
    } catch (err) {
      console.error('Failed to update status:', err);
      alert(err.message || 'Update failed');
    }
  };

  /* ----- INLINE PLANNED BUDGET EDIT ----- */
  const handlePlannedBudgetClick = (task) => {
    setEditingPlannedBudget(String(task._id || ''));
    setPlannedBudgetValue(String(task.plannedBudget || ''));
  };

  const handlePlannedBudgetSave = async (taskId) => {
    const task = tasks.find(t => t._id === taskId);
    if (!task) return;

    const newValue = parseFloat(plannedBudgetValue);
    if (isNaN(newValue)) {
      alert('Please enter a valid number');
      return;
    }

    // Optimistic update
    setTasks(prev =>
      prev.map(t =>
        t._id === taskId ? { ...t, plannedBudget: newValue } : t
      )
    );

    setEditingPlannedBudget('');
    setPlannedBudgetValue('');

    try {
      await api(`/api/marketingtasks/${taskId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...task,
          plannedBudget: newValue,
        }),
      });
    } catch (err) {
      console.error('Failed to update planned budget:', err);
      alert(err.message || 'Update failed');
    }
  };

  const handlePlannedBudgetCancel = () => {
    setEditingPlannedBudget('');
    setPlannedBudgetValue('');
  };

  /* ----- TEMPLATE MODAL ACTIONS ----- */
  const openNewTemplateModal = () => {
    setEditingTemplate({ _id: null, name: '', plannedBudget: '', notes: '' });
    setShowTemplateModal(true);
  };

  const saveTemplateToServer = (body, method, url) =>
    api(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then(r => r.json());

  const handleSaveTemplate = async () => {
    if (!editingTemplate?.name.trim()) return alert('Template name is required');
    try {
      if (!editingTemplate._id) {
        const created = await saveTemplateToServer(
          editingTemplate,
          'POST',
          '/api/marketingtemplates'
        );
        setTemplates(prev => [...prev, created]);
      } else {
        const updated = await saveTemplateToServer(
          editingTemplate,
          'PUT',
          `/api/marketingtemplates/${editingTemplate._id}`
        );
        setTemplates(prev => prev.map(t => (t._id === updated._id ? updated : t)));
      }
      setShowTemplateModal(false);
      setEditingTemplate(null);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteTemplate = async tplId => {
    if (!window.confirm('Delete this template?')) return;
    await api(`/api/marketingtemplates/${tplId}`, { method: 'DELETE' }).catch(console.error);
    setTemplates(prev => prev.filter(t => t._id !== tplId));
  };

  /* ----- RELEASE BUDGET SETTER ----- */
  const handleReleaseBudgetClick = (relId) => {
    setEditingReleaseBudget(relId);
    setReleaseBudgetValue(String(releaseBudgets[relId] ?? ''));
  };

  const handleReleaseBudgetSave = async (relId) => {
    const val = parseFloat(releaseBudgetValue);
    if (isNaN(val)) {
      alert('Please enter a valid number');
      return;
    }

    // Optimistic update
    setReleaseBudgets(prev => ({ ...prev, [relId]: val }));
    setEditingReleaseBudget(null);
    setReleaseBudgetValue('');

    try {
      await api('/api/releasebudgets', {
        method : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body   : JSON.stringify({ projectId, releaseId: relId, budget: val }),
      });
    } catch (err) {
      console.error('Failed to update release budget:', err);
      alert(err.message || 'Update failed');
    }
  };

  const handleReleaseBudgetCancel = () => {
    setEditingReleaseBudget(null);
    setReleaseBudgetValue('');
  };

  /* ----- DRAG & DROP ----- */
  const handleDragStart = (e, id) => {
    e.dataTransfer.setData('text/plain', id);
    setDraggingTaskId(id);
  };
  const handleDragEnd   = () => {
    setDraggingTaskId(null);
    setDragOverRelease(null);
  };
  const handleDrop      = async (e, newRelId) => {
    e.preventDefault();
    if (!draggingTaskId) return;
    const task = tasks.find(t => t._id === draggingTaskId);
    if (!task) return;

    try {
      const updated = await saveTaskToServer(
        { ...task, releaseId: newRelId === GENERAL_RELEASE_ID ? null : newRelId },
        'PUT',
        `/api/marketingtasks/${draggingTaskId}`
      );
      setTasks(prev => prev.map(t => (t._id === updated._id ? updated : t)));
    } catch (err) {
      console.error(err);
    }
    handleDragEnd();
  };

  /* ╔════════════════════════════════════════════════╗
     ║  5. QUILL CONFIG                               ║
     ╚════════════════════════════════════════════════╝ */
  const quillModules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link', 'image'],
      ['clean'],
    ],
  };
  const quillFormats = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'blockquote',
    'list',
    'bullet',
    'link',
    'image',
  ];
  const quillStyle = {
    background  : '#2a2a2a',
    color       : '#fff',
    borderRadius: 4,
    minHeight   : 200,
    maxHeight   : 300,
    overflowY   : 'auto',
    border      : 'none',
    marginBottom: 10,
  };

  /* ╔════════════════════════════════════════════════╗
     ║  6. RENDER                                     ║
     ╚════════════════════════════════════════════════╝ */
  return (
    <div style={{ marginTop: 40, color: '#fff' }}>
      <style dangerouslySetInnerHTML={{ __html: quillNoBorderCSS }} />
      <style>{`
        input[type="number"]::-webkit-inner-spin-button,
        input[type="number"]::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
      `}</style>

      {/* ───────── Overall Summary ───────── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 30 }}>
        <h2 style={{ margin: 0 }}>Marketing Breakdown</h2>
        
        {/* Template buttons */}
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={openNewTemplateModal}
            style={{
              background   : '#555',
              color        : '#fff',
              border       : 'none',
              borderRadius : 6,
              padding      : '8px 16px',
              cursor       : 'pointer',
              fontWeight   : 600,
              fontSize     : 14,
            }}
          >
            + Template
          </button>
          <button
            onClick={() => setShowTemplateModal(true)}
            style={{
              background   : '#555',
              color        : '#fff',
              border       : 'none',
              borderRadius : 6,
              padding      : '8px 16px',
              cursor       : 'pointer',
              fontWeight   : 600,
              fontSize     : 14,
            }}
          >
            Manage Templates
          </button>
        </div>
      </div>

      {/* Budget Overview Cards */}
      <div
        style={{
          display: 'flex',
          gap: 16,
          marginBottom: 30,
          flexWrap: 'wrap',
        }}
      >
        {/* Total Budget */}
        <div
          style={{
            background: '#1e1e1e',
            padding: '20px',
            borderRadius: 10,
            minWidth: 200,
            flex: '0 1 auto',
          }}
        >
          <div style={{ fontSize: 12, color: '#999', marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Total Budget
          </div>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#fff', marginBottom: 4 }}>
            €{budget.toLocaleString()}
          </div>
          {reactiveBudgetsTotal > 0 && (
            <div style={{ fontSize: 11, color: '#666' }}>
              Initial: €{initialBudget.toLocaleString()} + Reactive: €{reactiveBudgetsTotal.toLocaleString()}
            </div>
          )}
        </div>

        {/* Planned Spend */}
        <div
          style={{
            background: '#1e1e1e',
            padding: '20px',
            borderRadius: 10,
            minWidth: 200,
            flex: '0 1 auto',
          }}
        >
          <div style={{ fontSize: 12, color: '#999', marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Planned Spend
          </div>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#5bc0de', marginBottom: 4 }}>
            €{Math.round(totalPlanned).toLocaleString()}
          </div>
          <div style={{ fontSize: 11, color: '#999' }}>
            €{Math.round(budget - totalPlanned).toLocaleString()} remaining
          </div>
        </div>

        {/* Actual Spend */}
        <div
          style={{
            background: '#1e1e1e',
            padding: '20px',
            borderRadius: 10,
            minWidth: 200,
            flex: '0 1 auto',
          }}
        >
          <div style={{ fontSize: 12, color: '#999', marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Actual Spend
          </div>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#7eef4f', marginBottom: 4 }}>
            €{Math.round(totalFinal).toLocaleString()}
          </div>
          <div style={{ fontSize: 11, color: '#999' }}>
            €{Math.round(budget - totalFinal).toLocaleString()} remaining
          </div>
        </div>
      </div>

      {/* ───────── Kanban Columns ───────── */}
      <div style={{ display: 'flex', gap: 20, overflowX: 'auto' }}>
        {releasesAll.map(rel => {
          const relId    = rel._id;
          const relTasks = getTasksForRelease(relId);
          const isOver   = dragOverRelease === relId;

          const relBudget = releaseBudgets[relId] ?? null;
          const relPlanned = relTasks.reduce((s, t) => s + (+t.plannedBudget || 0), 0);
          const relSpend  = relTasks.reduce((s, t) => s + getFinalForTask(t), 0);
          const relLeft   = relBudget !== null ? relBudget - relPlanned : null;

          return (
            <div
              key={relId}
              style={{
                background   : isOver ? '#333' : '#1e1e1e',
                padding      : 10,
                borderRadius : 8,
                minWidth     : 300,
                flex         : '0 0 auto',
                display      : 'flex',
                flexDirection: 'column',
                gap          : 10,
              }}
              onDragOver={e => e.preventDefault()}
              onDragEnter={() => setDragOverRelease(relId)}
              onDragLeave={() => setDragOverRelease(null)}
              onDrop      ={e => handleDrop(e, relId)}
            >
              {/* column header */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  {relId !== GENERAL_RELEASE_ID && (
                    <img
                      src={rel.coverArt || emptyArt}
                      alt=""
                      style={{
                        width       : 50,
                        height      : 50,
                        objectFit   : 'cover',
                        borderRadius: 5,
                      }}
                    />
                  )}

                  <div style={{ flex: 1 }}>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, marginBottom: 6 }}>
                      {rel.title}
                    </h3>
                    
                    {/* Release Budget - inline */}
                    {relId !== GENERAL_RELEASE_ID && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                        {editingReleaseBudget === relId ? (
                          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                            <span style={{ color: '#999', fontSize: 13 }}>Budget: </span>
                            <input
                              type="number"
                              value={releaseBudgetValue}
                              onChange={(e) => setReleaseBudgetValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleReleaseBudgetSave(relId);
                                if (e.key === 'Escape') handleReleaseBudgetCancel();
                              }}
                              autoFocus
                              style={{
                                width: 70,
                                padding: '2px 6px',
                                background: '#333',
                                color: '#fff',
                                border: '1px solid #444',
                                borderRadius: 4,
                                fontSize: 13,
                                fontWeight: 600,
                                outline: 'none',
                                MozAppearance: 'textfield',
                              }}
                            />
                            <button
                              onClick={() => handleReleaseBudgetSave(relId)}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                padding: 0,
                                display: 'flex',
                                alignItems: 'center',
                                opacity: 0.7,
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                              onMouseLeave={(e) => e.currentTarget.style.opacity = '0.7'}
                              title="Save"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            </button>
                            <button
                              onClick={handleReleaseBudgetCancel}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                padding: 0,
                                display: 'flex',
                                alignItems: 'center',
                                opacity: 0.7,
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                              onMouseLeave={(e) => e.currentTarget.style.opacity = '0.7'}
                              title="Cancel"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                              </svg>
                            </button>
                          </div>
                        ) : (
                          <div style={{ fontSize: 13 }}>
                            <span style={{ color: '#999' }}>Budget: </span>
                            <span style={{ color: relBudget !== null ? '#fff' : '#666', fontWeight: 600 }}>
                              {relBudget !== null ? euro(relBudget) : 'Not Set'}
                            </span>
                            {relBudget !== null && (
                              <span style={{ 
                                color: relLeft > 0 ? '#5bc0de' : '#999', 
                                fontSize: 11, 
                                marginLeft: 6,
                                fontWeight: relLeft > 0 ? 600 : 400,
                              }}>
                                ({euro(Math.round(relLeft))} left)
                              </span>
                            )}
                            <button
                              onClick={() => handleReleaseBudgetClick(relId)}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                padding: 0,
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                marginLeft: 4,
                              }}
                              title="Edit release budget"
                            >
                              <img
                                src={pencilIcon}
                                alt="Edit"
                                style={{
                                  width: 10,
                                  height: 10,
                                  opacity: 0.5,
                                  filter: 'brightness(0) saturate(100%) invert(60%)',
                                }}
                              />
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* tasks */}
              {relTasks.length === 0 ? (
                <p style={{ color: '#999', margin: '10px 0' }}>No tasks yet.</p>
              ) : (
                relTasks.map(task => {
                  const invs  = getInvoicesForTask(task._id);
                  const final = getFinalForTask(task);

                  const cardStyle = {
                    background  : task.status === 'Completed' ? '#1e3823' : '#2c2c2c',
                    opacity     : task.status === 'Completed' ? 0.7 : 1,
                    padding     : 8,
                    borderRadius: 5,
                  };
                  const nameStyle = {
                  };

                  return (
                    <div
                      key={task._id}
                      draggable
                      onDragStart={e => handleDragStart(e, task._id)}
                      onDragEnd  ={handleDragEnd}
                      style={cardStyle}
                    >
                      {/* top row - name and edit button */}
                      <div
                        style={{
                          display       : 'flex',
                          justifyContent: 'space-between',
                          alignItems    : 'flex-start',
                          marginBottom  : 8,
                        }}
                      >
                        <strong style={{ ...nameStyle, fontSize: 14 }}>{task.name}</strong>
                        <button
                          onClick={() => {
                            setEditingTask({ ...task });
                            setShowTaskModal(true);
                          }}
                          style={{
                            background: 'transparent',
                            border    : 'none',
                            color     : '#999',
                            cursor    : 'pointer',
                            fontSize  : 12,
                            padding   : 0,
                          }}
                        >
                          Edit
                        </button>
                      </div>

                      {/* Budget info */}
                      <div style={{ marginBottom: 8 }}>
                        <div style={{ display: 'flex', gap: 16, fontSize: 13 }}>
                          <div>
                            <div style={{ color: '#999', fontSize: 11, marginBottom: 2 }}>Planned</div>
                            {editingPlannedBudget && String(editingPlannedBudget) === String(task._id) ? (
                              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                <input
                                  type="number"
                                  value={plannedBudgetValue}
                                  onChange={(e) => setPlannedBudgetValue(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handlePlannedBudgetSave(task._id);
                                    if (e.key === 'Escape') handlePlannedBudgetCancel();
                                  }}
                                  onBlur={handlePlannedBudgetCancel}
                                  autoFocus
                                  style={{
                                    width: 70,
                                    padding: '2px 6px',
                                    background: '#333',
                                    color: '#fff',
                                    border: '1px solid #444',
                                    borderRadius: 4,
                                    fontSize: 13,
                                    fontWeight: 600,
                                    outline: 'none',
                                    MozAppearance: 'textfield',
                                  }}
                                />
                                <button
                                  onClick={() => handlePlannedBudgetSave(task._id)}
                                  style={{
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    opacity: 0.7,
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                                  onMouseLeave={(e) => e.currentTarget.style.opacity = '0.7'}
                                  title="Save"
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12" />
                                  </svg>
                                </button>
                                <button
                                  onClick={handlePlannedBudgetCancel}
                                  style={{
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    opacity: 0.7,
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                                  onMouseLeave={(e) => e.currentTarget.style.opacity = '0.7'}
                                  title="Cancel"
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                  </svg>
                                </button>
                              </div>
                            ) : (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <span style={{ color: '#5bc0de', fontWeight: 600 }}>
                                  {euro(task.plannedBudget || 0)}
                                </span>
                                <button
                                  onClick={() => handlePlannedBudgetClick(task)}
                                  style={{
                                    background: 'transparent',
                                    border: 'none',
                                    padding: 0,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                  }}
                                  title="Edit planned budget"
                                >
                                  <img
                                    src={pencilIcon}
                                    alt="Edit"
                                    style={{
                                      width: 10,
                                      height: 10,
                                      opacity: 0.5,
                                      filter: 'brightness(0) saturate(100%) invert(60%)',
                                    }}
                                  />
                                </button>
                              </div>
                            )}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ color: '#999', fontSize: 11, marginBottom: 2 }}>Actual</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                              <span style={{ color: '#7eef4f', fontWeight: 600 }}>
                                {euro(Math.round(final))}
                              </span>
                              {/* invoice chips inline */}
                              {invs.length > 0 && invs.map(inv => (
                                <a
                                  key={inv._id}
                                  href={`/invoice/${inv._id}`}
                                  title={`Invoice €${inv.amount}`}
                                  style={{
                                    background   : '#3a3a3a',
                                    color        : '#999',
                                    padding      : '2px 6px',
                                    borderRadius : 3,
                                    textDecoration: 'none',
                                    fontWeight   : 600,
                                    display      : 'flex',
                                    alignItems   : 'center',
                                    gap          : 3,
                                    fontSize     : 10,
                                  }}
                                >
                                  <img
                                    src={invoiceIcon}
                                    alt=""
                                    style={{
                                      width: 9,
                                      height: 9,
                                      filter: 'brightness(0) saturate(100%) invert(60%)',
                                    }}
                                  />
                                  €{Math.round(inv.amount)}
                                </a>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* status */}
                      <div>
                        <select
                          value={task.status}
                          onChange={e => handleStatusChange(task._id, e.target.value)}
                          style={{
                            background  : statusBg(task.status),
                            color       : '#fff',
                            border      : 'none',
                            borderRadius: 5,
                            padding     : '6px 10px',
                            fontSize    : 12,
                            cursor      : 'pointer',
                            fontWeight  : 600,
                            width       : '100%',
                          }}
                        >
                          <option value="Not Started">Not Started</option>
                          <option value="In Progress">In Progress</option>
                          <option value="Completed">Completed</option>
                        </select>
                      </div>
                    </div>
                  );
                })
              )}

              <button
                onClick={() => openNewTaskModal(relId)}
                style={{
                  marginTop   : 'auto',
                  background  : '#555',
                  color       : 'white',
                  border      : 'none',
                  borderRadius: '5px',
                  padding     : '8px 15px',
                  cursor      : 'pointer',
                  fontWeight  : 'bold',
                }}
              >
                + Add Task
              </button>
            </div>
          );
        })}
      </div>

      {/* ═════════════ TASK MODAL ═════════════ */}
      {showTaskModal && editingTask && (
        <div
          style={{
            position : 'fixed',
            inset    : 0,
            background: 'rgba(0,0,0,0.7)',
            display  : 'flex',
            justifyContent: 'center',
            alignItems     : 'center',
            zIndex   : 9999,
          }}
        >
          <div
            style={{
              background : '#1e1e1e',
              padding    : '30px',
              borderRadius: '8px',
              width      : '500px',
              maxHeight  : '80vh',
              overflowY  : 'auto',
            }}
          >
            <h2 style={{ marginTop: 0, marginBottom: 24 }}>
              {editingTask._id ? 'Edit Campaign' : 'Add Campaign'}
            </h2>

            {/* name */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>Campaign Name</label>
              <input
                type="text"
                value={editingTask.name}
                onChange={e => setEditingTask(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. TikTok Campaign"
                style={{
                  width        : '100%',
                  padding      : '10px 12px',
                  background   : '#333',
                  color        : '#fff',
                  border       : 'none',
                  borderRadius : 6,
                  fontSize     : 14,
                  boxSizing    : 'border-box',
                }}
              />
            </div>

            {/* planned budget - always show */}
            <div style={{ marginBottom: editingTask._id ? 16 : 24 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>Planned Budget</label>
              <input
                type="number"
                value={editingTask.plannedBudget}
                onChange={e =>
                  setEditingTask(prev => ({ ...prev, plannedBudget: e.target.value }))
                }
                placeholder="€0"
                style={{
                  width        : '100%',
                  padding      : '10px 12px',
                  background   : '#333',
                  color        : '#fff',
                  border       : 'none',
                  borderRadius : 6,
                  fontSize     : 14,
                  boxSizing    : 'border-box',
                }}
              />
            </div>

            {/* Only show these fields when editing existing task */}
            {editingTask._id && (
              <>
                {/* status */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>Status</label>
                  <select
                    value={editingTask.status}
                    onChange={e =>
                      setEditingTask(prev => ({ ...prev, status: e.target.value }))
                    }
                    style={{
                      background   : statusBg(editingTask.status),
                      color        : '#fff',
                      border       : 'none',
                      borderRadius : 6,
                      padding      : '10px 12px',
                      cursor       : 'pointer',
                      width        : '100%',
                      fontSize     : 14,
                      boxSizing    : 'border-box',
                    }}
                  >
                    <option value="Not Started">Not Started</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Completed">Completed</option>
                  </select>
                </div>

                {/* final budget (edit only) */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>Final Budget</label>
                  <input
                    type="number"
                    value={editingTask.finalBudget}
                    onChange={e =>
                      setEditingTask(prev => ({ ...prev, finalBudget: e.target.value }))
                    }
                    placeholder="€0"
                    style={{
                      width        : '100%',
                      padding      : '10px 12px',
                      background   : '#333',
                      color        : '#fff',
                      border       : 'none',
                      borderRadius : 6,
                      fontSize     : 14,
                      boxSizing    : 'border-box',
                    }}
                  />
                </div>

                {/* notes (edit only) */}
                <div style={{ marginBottom: 24 }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>
                    Notes / Details (Rich Text)
                  </label>
                  <ReactQuill
                    value={editingTask.notes || ''}
                    onChange={val => setEditingTask(prev => ({ ...prev, notes: val }))}
                    modules={quillModules}
                    formats={quillFormats}
                    style={quillStyle}
                  />
                </div>
              </>
            )}

            {/* buttons */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              {editingTask._id && (
                <button
                  onClick={handleDeleteTask}
                  style={{
                    padding      : '10px 20px',
                    background   : '#d9534f',
                    color        : '#fff',
                    border       : 'none',
                    borderRadius : 6,
                    cursor       : 'pointer',
                    fontWeight   : 600,
                  }}
                >
                  Delete
                </button>
              )}
              <button
                onClick={() => {
                  setShowTaskModal(false);
                  setEditingTask(null);
                }}
                style={{
                  padding      : '10px 20px',
                  background   : '#555',
                  color        : '#fff',
                  border       : 'none',
                  borderRadius : 6,
                  cursor       : 'pointer',
                  fontWeight   : 600,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTask}
                style={{
                  padding      : '10px 20px',
                  background   : '#28a745',
                  color        : '#fff',
                  border       : 'none',
                  borderRadius : 6,
                  cursor       : 'pointer',
                  fontWeight   : 600,
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═════════════ DETAILS MODAL ═════════════ */}
      {showDetailsModal && detailsTask && (
        <div
          style={{
            position : 'fixed',
            inset    : 0,
            background: 'rgba(0,0,0,0.7)',
            display  : 'flex',
            justifyContent: 'center',
            alignItems     : 'center',
            zIndex   : 9999,
          }}
        >
          <div
            style={{
              background : '#1e1e1e',
              padding    : '20px 40px',
              borderRadius: '8px',
              width      : '600px',
              maxHeight  : '80vh',
              overflowY  : 'auto',
            }}
          >
            <h2 style={{ marginTop: 0 }}>Campaign Details</h2>
            <p>
              <strong>Name:</strong> {detailsTask.name}
            </p>
            <p>
              <strong>Status:</strong> {detailsTask.status}
            </p>
            <p>
              <strong>Planned Budget:</strong> {euro(detailsTask.plannedBudget || 0)}
            </p>
            <p>
              <strong>Final Budget:</strong> {euro(getFinalForTask(detailsTask))}
            </p>

            {/* invoice list */}
            <div style={{ marginTop: 10 }}>
              <strong>Invoices:</strong>{' '}
              {getInvoicesForTask(detailsTask._id).length === 0 ? (
                <span style={{ color: '#999' }}>None</span>
              ) : (
                <ul style={{ marginTop: 4, paddingLeft: 18 }}>
                  {getInvoicesForTask(detailsTask._id).map(inv => (
                    <li key={inv._id}>
                      <a href={`/invoice/${inv._id}`} style={{ color: '#9cf' }}>
                        {euro(inv.amount)} — {inv.description || inv.fileName}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div style={{ marginTop: 10 }}>
              <strong>Notes / Details:</strong>
              <div
                style={{
                  marginTop   : 5,
                  background  : '#2a2a2a',
                  padding     : 10,
                  borderRadius: 5,
                }}
                dangerouslySetInnerHTML={{ __html: detailsTask.notes || '' }}
              />
            </div>

            <div style={{ textAlign: 'right', marginTop: 20 }}>
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setDetailsTask(null);
                }}
                style={{
                  padding      : '8px 15px',
                  background   : '#555',
                  color        : '#fff',
                  border       : 'none',
                  borderRadius : 4,
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═════════════ TEMPLATE MODAL ═════════════ */}
      {showTemplateModal && (
        <div
          style={{
            position : 'fixed',
            inset    : 0,
            background: 'rgba(0,0,0,0.7)',
            display  : 'flex',
            justifyContent: 'center',
            alignItems     : 'center',
            zIndex   : 9999,
          }}
        >
          <div
            style={{
              background : '#1e1e1e',
              padding    : '20px 40px',
              borderRadius: '8px',
              width      : '500px',
              maxHeight  : '80vh',
              overflowY  : 'auto',
            }}
          >
            <h2 style={{ marginTop: 0 }}>
              {editingTemplate ? 'Edit Template' : 'Manage Templates'}
            </h2>

            {/* ─── Single-template form ─── */}
            {editingTemplate && (
              <>
                <div style={{ marginBottom: 10 }}>
                  <label style={{ display: 'block', marginBottom: 5 }}>
                    Template Name:
                  </label>
                  <input
                    type="text"
                    value={editingTemplate.name}
                    onChange={e =>
                      setEditingTemplate(prev => ({ ...prev, name: e.target.value }))
                    }
                    style={{
                      width        : '100%',
                      padding      : 8,
                      background   : '#333',
                      color        : '#fff',
                      border       : 'none',
                      borderRadius : 4,
                      marginBottom : 10,
                    }}
                  />

                  <label style={{ display: 'block', marginBottom: 5 }}>
                    Planned Budget:
                  </label>
                  <input
                    type="number"
                    value={editingTemplate.plannedBudget}
                    onChange={e =>
                      setEditingTemplate(prev => ({
                        ...prev,
                        plannedBudget: e.target.value,
                      }))
                    }
                    style={{
                      width        : '100%',
                      padding      : 8,
                      background   : '#333',
                      color        : '#fff',
                      border       : 'none',
                      borderRadius : 4,
                      marginBottom : 10,
                    }}
                  />

                  <label style={{ display: 'block', marginBottom: 5 }}>
                    Notes / Details (Rich Text):
                  </label>
                  <ReactQuill
                    value={editingTemplate.notes || ''}
                    onChange={val =>
                      setEditingTemplate(prev => ({ ...prev, notes: val }))
                    }
                    modules={quillModules}
                    formats={quillFormats}
                    style={{ ...quillStyle, minHeight: 150 }}
                  />
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                  <button
                    onClick={() => setEditingTemplate(null)}
                    style={{
                      padding      : '8px 15px',
                      background   : '#555',
                      color        : '#fff',
                      border       : 'none',
                      borderRadius : 4,
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveTemplate}
                    style={{
                      padding      : '8px 15px',
                      background   : '#28a745',
                      color        : '#fff',
                      border       : 'none',
                      borderRadius : 4,
                    }}
                  >
                    Save
                  </button>
                </div>
              </>
            )}

            {/* ─── Template list view ─── */}
            {!editingTemplate && (
              <>
                <div style={{ marginBottom: 10 }}>
                  <button
                    onClick={openNewTemplateModal}
                    style={{
                      padding      : '8px 15px',
                      background   : '#28a745',
                      color        : '#fff',
                      border       : 'none',
                      borderRadius : 4,
                    }}
                  >
                    + New Template
                  </button>
                </div>

                {templates.length === 0 ? (
                  <p style={{ color: '#999' }}>No templates yet.</p>
                ) : (
                  templates.map(tpl => (
                    <div
                      key={tpl._id}
                      style={{
                        background  : '#2c2c2c',
                        padding     : 8,
                        borderRadius: 5,
                        marginBottom: 6,
                      }}
                    >
                      <div
                        style={{
                          display       : 'flex',
                          justifyContent: 'space-between',
                          alignItems    : 'center',
                        }}
                      >
                        <strong>{tpl.name}</strong>
                        <span style={{ fontSize: '.85rem' }}>
                          ({euro(tpl.plannedBudget || 0)})
                        </span>
                      </div>
                      <div style={{ marginTop: 6 }}>
                        <button
                          onClick={() => setEditingTemplate({ ...tpl })}
                          style={{
                            marginRight : 8,
                            background  : '#555',
                            color       : '#fff',
                            border       : 'none',
                            borderRadius : 4,
                            padding      : '5px 10px',
                            fontSize     : '.8rem',
                            cursor       : 'pointer',
                          }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteTemplate(tpl._id)}
                          style={{
                            background  : '#d9534f',
                            color       : '#fff',
                            border       : 'none',
                            borderRadius : 4,
                            padding      : '5px 10px',
                            fontSize     : '.8rem',
                            cursor       : 'pointer',
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))
                )}

                <div style={{ textAlign: 'right', marginTop: 10 }}>
                  <button
                    onClick={() => setShowTemplateModal(false)}
                    style={{
                      padding      : '8px 15px',
                      background   : '#555',
                      color        : '#fff',
                      border       : 'none',
                      borderRadius : 4,
                    }}
                  >
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default MarketingBreakdown;